import type { NarrationState } from '../net/useRoom.js';
import lowerthirdPlate from '../assets/ornaments/lowerthird-plate.webp';

/**
 * NarrationBanner — the Ringmaster's caption, shown on every hand screen. Phones
 * never play audio; caption-only. The caption rides an engraved lower-third
 * plate (lowerthird-plate.webp) at phone scale, in the Grenze display face, and
 * fades in when the text changes. A pulsing gold speaking-dot marks when the
 * narrator is actively voicing (isSpeaking).
 *
 * `offsetBottom` lifts the banner up so it can sit ABOVE the host's fixed
 * RingmasterBar (one banner instance, never colliding).
 */
export function NarrationBanner({
  narration,
  offsetBottom = 0,
}: {
  narration: NarrationState | null;
  offsetBottom?: number;
}) {
  if (!narration?.text) return null;
  // "Speaking" = this device has an audio clip to play for the current line
  // (captionOnly lines have no audioId → the dot rests). Mirrors the existing
  // audioId-vs-caption split; no new state.
  const speaking = !!narration.audioId && !narration.captionOnly;
  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: offsetBottom,
        left: 0,
        right: 0,
        zIndex: 50,
        padding: 'var(--space-3) var(--space-4)',
        paddingBottom: offsetBottom
          ? 'var(--space-2)'
          : 'max(var(--space-4), env(safe-area-inset-bottom, 0px) + var(--space-2))',
        // Same corner reservation as FixedActionBar: on a screen with no action
        // bar this plate is the only thing in the bottom band, so it has to keep
        // clear of a bottom-left corner control too. Zero when unset.
        paddingLeft: 'calc(var(--space-4) + var(--rm-corner-inset, 0px))',
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div className="nb-plate">
        <img src={lowerthirdPlate} alt="" aria-hidden className="nb-plate-bg" />
        <div className="nb-plate-body">
          <span className={`nb-dot${speaking ? ' is-speaking' : ''}`} aria-hidden />
          <p key={narration.text} className="nb-caption">
            {narration.text}
          </p>
        </div>
      </div>
      <style>{`
        .nb-plate {
          position: relative; max-width: 560px; width: 100%;
          border-radius: var(--radius-lg); overflow: hidden;
        }
        .nb-plate-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.9; }
        .nb-plate-body {
          position: relative; z-index: 1;
          display: flex; align-items: center; justify-content: center; gap: 10px;
          padding: 10px var(--space-5);
          background: linear-gradient(0deg, rgba(6,4,6,0.72) 0%, rgba(6,4,6,0.35) 100%);
        }
        .nb-dot {
          width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
          background: var(--gold-4, #c9923f); opacity: 0.4;
        }
        .nb-dot.is-speaking { opacity: 1; animation: nb-dot-pulse 1.1s ease-in-out infinite; }
        @keyframes nb-dot-pulse { 0%,100% { opacity: 0.5; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1.15); } }
        .nb-caption {
          font-family: var(--font-display); font-size: 14px; letter-spacing: 0.02em;
          color: var(--text-primary); margin: 0; text-align: center; line-height: 1.5;
          text-shadow: 0 1px 6px rgba(0,0,0,0.8);
          animation: nb-caption-in 0.4s var(--ease-standard);
        }
        @keyframes nb-caption-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) {
          .nb-dot.is-speaking, .nb-caption { animation: none; }
        }
      `}</style>
    </div>
  );
}
